import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import ImageUploader from "@/components/upload/ImageUploader";
import { Star, ThumbsUp, Edit, Trash2, X, ImageIcon, ShieldCheck, MessageCircle, ChevronDown, Camera } from "lucide-react";
import { toast } from "sonner";

// ---------- Small building blocks ----------
export const StarRow = ({ value = 0, size = 4, onSelect }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="inline-flex items-center gap-0.5">
      {stars.map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(n)}
          className={onSelect ? "hover:scale-110 transition" : "pointer-events-none"}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          data-testid={onSelect ? `star-${n}` : undefined}
        >
          <Star className={`w-${size} h-${size} ${n <= value ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
        </button>
      ))}
    </div>
  );
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < day) return "today";
  const days = Math.floor(diffMs / day);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
  return d.toLocaleDateString("en-IN", { year: "numeric", month: "short" });
};

// ---------- Image lightbox ----------
const Lightbox = ({ images, index, onClose, onIndex }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIndex((index - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") onIndex((index + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onIndex]);
  const img = images[index];
  if (!img) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={onClose} data-testid="review-lightbox">
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-4 right-4 text-white/90 hover:text-white" aria-label="Close"><X className="w-7 h-7" /></button>
      <button onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + images.length) % images.length); }} className="absolute left-4 text-white text-3xl px-3 select-none">‹</button>
      <img src={img.url || img} alt="" className="max-h-[85vh] max-w-[95vw] object-contain rounded" onClick={(e) => e.stopPropagation()} />
      <button onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % images.length); }} className="absolute right-4 text-white text-3xl px-3 select-none">›</button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs">{index + 1} / {images.length}</div>
    </div>
  );
};

// ---------- Review submission modal ----------
const ReviewForm = ({ productId, orderId, existing, onClose, onSaved }) => {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [title, setTitle] = useState(existing?.title || "");
  const [comment, setComment] = useState(existing?.comment || "");
  const [images, setImages] = useState(existing?.images || []);
  const [busy, setBusy] = useState(false);
  const isEdit = !!existing;

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please select a star rating"); return; }
    if (comment.trim().length < 3) { toast.error("Please write your review"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/reviews/${existing.id}`, { rating, title, comment, images });
        toast.success("Review updated");
      } else {
        await api.post(`/products/${productId}/reviews`, { product_id: productId, order_id: orderId, rating, title, comment, images });
        toast.success("Review submitted — thank you!");
      }
      onSaved?.();
      onClose();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Could not submit review");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="review-form-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-4 py-3 flex items-center justify-between z-10">
          <h2 className="font-display font-bold">{isEdit ? "Edit your review" : "Write a review"}</h2>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Overall Rating *</label>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`} className="hover:scale-110 transition">
                    <Star className={`w-8 h-8 ${n <= rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
                  </button>
                ))}
              </div>
              <span className="text-sm text-muted-foreground">{rating > 0 ? ["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating] : ""}</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Title <span className="normal-case text-muted-foreground">(optional)</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Great value for money" className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white text-sm" data-testid="review-title" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your Review *</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={5} maxLength={4000} placeholder="Share your experience with this product — quality, delivery, effectiveness on your crop, etc." className="mt-1 w-full px-3 py-2 border border-border rounded-lg bg-white text-sm" required data-testid="review-comment" />
            <div className="text-[11px] text-muted-foreground text-right">{comment.length}/4000</div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Add Photos <span className="normal-case text-muted-foreground">(up to 5)</span></label>
            <div className="mt-2">
              <ImageUploader value={images} onChange={setImages} maxFiles={5} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Cancel</button>
            <button disabled={busy} type="submit" className="bg-primary hover:bg-primary-hover text-white font-bold text-sm px-5 py-2 rounded-lg disabled:opacity-50" data-testid="review-submit">
              {busy ? "Submitting…" : isEdit ? "Save Changes" : "Submit Review"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// ---------- Individual review card ----------
const ReviewCard = ({ review, currentUserId, onEdit, onDelete, onHelpful, onOpenImg }) => {
  const isOwn = currentUserId === review.user_id;
  return (
    <div className="card-fah p-4 sm:p-5" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold shrink-0">
            {review.user_name?.[0]?.toUpperCase() || "U"}
          </div>
          <div>
            <div className="font-semibold text-sm">{review.user_name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <StarRow value={review.rating} size={3} />
              <span className="text-[11px] text-muted-foreground">{timeAgo(review.created_at)}</span>
              {review.updated_at && review.updated_at !== review.created_at && (
                <span className="text-[10px] text-muted-foreground italic">edited</span>
              )}
            </div>
          </div>
        </div>
        {review.is_verified_purchase && (
          <span className="text-[10px] font-bold uppercase text-green-700 bg-green-100 px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap" data-testid={`verified-badge-${review.id}`}>
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
      </div>

      {review.title && <div className="font-semibold text-sm mt-3">{review.title}</div>}
      <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line mt-2">{review.comment}</p>

      {review.images?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {review.images.map((img, i) => (
            <button key={i} onClick={() => onOpenImg(review.images, i)} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-border" data-testid={`review-image-${review.id}-${i}`}>
              <img src={img} alt="" loading="lazy" className="w-full h-full object-cover hover:opacity-80 transition" />
            </button>
          ))}
        </div>
      )}

      {review.admin_reply && (
        <div className="mt-3 pl-3 border-l-2 border-primary bg-primary/5 rounded-r p-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-primary flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Response from FarmersAgroHub</div>
          <p className="text-xs text-foreground/80 mt-1 whitespace-pre-line">{review.admin_reply}</p>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-xs">
        <button onClick={() => onHelpful(review)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary" data-testid={`helpful-${review.id}`}>
          <ThumbsUp className="w-3.5 h-3.5" /> Helpful {review.helpful_count ? `(${review.helpful_count})` : ""}
        </button>
        {isOwn && (
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => onEdit(review)} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`edit-${review.id}`}><Edit className="w-3.5 h-3.5" /> Edit</button>
            <button onClick={() => onDelete(review)} className="text-destructive hover:underline inline-flex items-center gap-1 ml-2" data-testid={`del-${review.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Main section ----------
const ProductReviews = ({ productId, product }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [sort, setSort] = useState("recent");
  const [withImages, setWithImages] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [eligibility, setEligibility] = useState({ can_review: false, order_id: null, checked: false });
  const [lightbox, setLightbox] = useState({ open: false, images: [], idx: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${productId}/reviews/stats`);
      setStats(data);
    } catch { /* silent */ }
  }, [productId]);

  const fetchReviews = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sort, with_images: String(withImages) });
      const { data } = await api.get(`/products/${productId}/reviews?${params.toString()}`);
      setReviews(data);
    } catch { /* silent */ }
  }, [productId, sort, withImages]);

  const checkEligibility = useCallback(async () => {
    if (!user) { setEligibility({ can_review: false, order_id: null, checked: true }); return; }
    try {
      const { data } = await api.get("/my/pending-reviews");
      const match = data.find((p) => p.product_id === productId);
      setEligibility({ can_review: !!match, order_id: match?.order_id || null, checked: true });
    } catch { setEligibility({ can_review: false, order_id: null, checked: true }); }
  }, [user, productId]);

  useEffect(() => { fetchStats(); fetchReviews(); checkEligibility(); }, [fetchStats, fetchReviews, checkEligibility]);

  const refreshAll = () => { fetchStats(); fetchReviews(); checkEligibility(); };

  const onHelpful = async (review) => {
    if (!user) { toast.error("Please login to mark reviews helpful"); return; }
    try {
      const { data } = await api.post(`/reviews/${review.id}/helpful`);
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, helpful_count: data.helpful_count } : r)));
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Could not save");
    }
  };

  const onEdit = (review) => { setEditing(review); setFormOpen(true); };
  const onDelete = async (review) => {
    if (!window.confirm("Delete your review?")) return;
    try { await api.delete(`/reviews/${review.id}`); toast.success("Review deleted"); refreshAll(); }
    catch (ex) { toast.error(formatApiErrorDetail(ex.response?.data?.detail) || "Delete failed"); }
  };

  const openLightbox = (images, idx) => setLightbox({ open: true, images: images.map((u) => (typeof u === "string" ? { url: u } : u)), idx });

  const dist = stats?.distribution || { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  const total = stats?.total_ratings || 0;
  const ownReview = user ? reviews.find((r) => r.user_id === user.id) : null;

  return (
    <section className="mt-12" data-testid="product-reviews">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold">Ratings &amp; Reviews</h2>
        {eligibility.can_review && !ownReview && (
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-primary hover:bg-primary-hover text-white text-sm font-bold px-4 py-2 rounded-lg" data-testid="btn-write-review">
            Write a Review
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="card-fah p-5 grid sm:grid-cols-[220px_1fr] gap-6" data-testid="reviews-summary">
        <div className="text-center sm:border-r sm:border-border sm:pr-6">
          <div className="text-5xl font-bold text-primary" data-testid="avg-rating">{stats?.avg_rating || product.rating || "—"}</div>
          <StarRow value={Math.round(stats?.avg_rating || product.rating || 0)} size={5} />
          <div className="text-xs text-muted-foreground mt-1" data-testid="total-ratings">
            {total} rating{total === 1 ? "" : "s"} · {stats?.total_reviews || 0} review{(stats?.total_reviews || 0) === 1 ? "" : "s"}
          </div>
        </div>
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((n) => {
            const count = dist[String(n)] || 0;
            const pct = total ? (count / total) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs" data-testid={`dist-${n}`}>
                <span className="w-8 flex items-center gap-0.5 font-semibold">{n}<Star className="w-3 h-3 fill-secondary text-secondary" /></span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-10 text-right text-muted-foreground tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Customer image gallery */}
      {stats?.gallery?.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase tracking-wide font-bold text-muted-foreground mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Customer Photos</div>
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="customer-gallery">
            {stats.gallery.map((g, i) => (
              <button key={i} onClick={() => openLightbox(stats.gallery, i)} className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-lg overflow-hidden border border-border" data-testid={`gallery-img-${i}`}>
                <img src={g.url} alt={`by ${g.user_name}`} loading="lazy" className="w-full h-full object-cover hover:opacity-80 transition" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sort + filter bar */}
      <div className="mt-6 flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <div className="text-sm font-semibold">Filter &amp; Sort:</div>
        <div className="relative">
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-xs font-semibold border border-border rounded-full pl-3 pr-7 py-1.5 bg-white appearance-none" data-testid="review-sort">
            <option value="recent">Most Recent</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
            <option value="helpful">Most Helpful</option>
          </select>
          <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        </div>
        <button onClick={() => setWithImages((v) => !v)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${withImages ? "bg-primary text-white border-primary" : "bg-white border-border"}`} data-testid="filter-with-images">
          <ImageIcon className="w-3 h-3 inline mr-1" /> With Photos
        </button>
      </div>

      {/* Reviews list */}
      <div className="mt-4 space-y-3" data-testid="reviews-list">
        {reviews.length === 0 && (
          <div className="card-fah p-6 text-center text-sm text-muted-foreground">
            No reviews yet. {eligibility.can_review && !ownReview ? "Be the first to review!" : "Check back soon!"}
          </div>
        )}
        {reviews.map((r) => (
          <ReviewCard
            key={r.id}
            review={r}
            currentUserId={user?.id}
            onEdit={onEdit}
            onDelete={onDelete}
            onHelpful={onHelpful}
            onOpenImg={openLightbox}
          />
        ))}
      </div>

      {formOpen && (
        <ReviewForm
          productId={productId}
          orderId={eligibility.order_id}
          existing={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={refreshAll}
        />
      )}
      {lightbox.open && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.idx}
          onClose={() => setLightbox({ open: false, images: [], idx: 0 })}
          onIndex={(i) => setLightbox((prev) => ({ ...prev, idx: i }))}
        />
      )}
    </section>
  );
};

export default ProductReviews;
